<template>
  <section class="customer-overview-tab">
    <div class="customer-overview-tab__grid">
      <div class="customer-overview-tab__main">
        <article class="sale-summary-card sale-summary-card--financial customer-overview-health">
          <header class="sale-summary-card__header">
            <h4>{{ overviewText.health.title }}</h4>
            <span class="sale-summary-pill" :class="healthStatusClass">{{ healthStatusLabel }}</span>
          </header>

          <div class="customer-overview-health__content">
            <div class="sale-summary-ring" :style="ringStyle">
              <div class="sale-summary-ring__inner">
                <strong>{{ ringPercentLabel }}</strong>
                <small>{{ overviewText.health.liquidity }}</small>
              </div>
            </div>

            <div class="customer-overview-health__details">
              <div class="sale-summary-progress">
                <div class="sale-summary-progress__head">
                  <span>{{ overviewText.health.collectedVsPending }}</span>
                  <strong>{{ formatMoney(totalBilled) }} / {{ formatMoney(totalForecast) }}</strong>
                </div>

                <div class="sale-summary-progress__track">
                  <div class="sale-summary-progress__fill is-income" :style="collectedProgressStyle"></div>
                </div>
              </div>

              <p class="sale-summary-budget-health__copy customer-overview-health__summary">
                {{ healthSummaryText }}
              </p>

              <div v-if="showExposureWarning" class="customer-overview-warning">
                <strong>{{ overviewText.health.exposureWarningTitle }}</strong>
                <p>{{ exposureWarningText }}</p>
              </div>
            </div>
          </div>
        </article>

        <RecentActivityCard
          :entity-id="customerId"
          entity-type="customer"
          :per-page="4"
          @view-history="$emit('open-history')"
        />
      </div>

      <aside class="customer-overview-tab__side">
        <PaymentFlowCard
          :flow="overviewFlow"
          :title="overviewText.paymentFlow.title"
          :total-pending-label="overviewText.paymentFlow.totalPending"
          :overdue-label="overviewText.paymentFlow.overdue"
          :next7-days-label="overviewText.paymentFlow.next7Days"
          :rest-of-month-label="overviewText.paymentFlow.restOfMonth"
          top-border-color="#3bd42e"
        />
      </aside>
    </div>
  </section>
</template>

<script>
import { formatCurrency } from '@/utils/numberFormat';
import { toAmount } from '@/utils/numberValue';
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import PaymentFlowCard from '@/components/common/PaymentFlowCard.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerDetailOverviewTab',
  components: {
    RecentActivityCard,
    PaymentFlowCard,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    customerMetrics: {
      type: Object,
      default: null,
    },
    customerOverview: {
      type: Object,
      default: null,
    },
  },
  emits: ['open-history'],
  data() {
    return {
      text,
    };
  },
  computed: {
    overviewText() {
      return this.text?.detail?.overview || {
        health: {
          title: '',
          liquidity: '',
          collectedVsPending: '',
          noForecastSummary: '',
          pendingSummary: null,
          exposureWarningTitle: '',
          exposureWarningText: null,
          status: {
            risk: '',
            attention: '',
            healthy: '',
          },
        },
        paymentFlow: {
          title: '',
          totalPending: '',
          overdue: '',
          next7Days: '',
          restOfMonth: '',
        },
      };
    },
    customerId() {
      return Number(this.customer?.id || 0);
    },
    overviewHealth() {
      return this.customerOverview?.health && typeof this.customerOverview.health === 'object'
        ? this.customerOverview.health
        : {};
    },
    overviewFlow() {
      return this.customerOverview?.payment_flow && typeof this.customerOverview.payment_flow === 'object'
        ? this.customerOverview.payment_flow
        : {};
    },
    totalForecast() {
      return Number(
        this.overviewHealth?.forecast_total
        ?? this.customerMetrics?.income_forecast
        ?? this.customer?.income_forecast
        ?? this.customer?.cxc
        ?? 0
      ) || 0;
    },
    totalBilled() {
      return Number(
        this.overviewHealth?.paid_total
        ?? this.customerMetrics?.income_billed
        ?? this.customer?.income_billed
        ?? this.customer?.total_billed
        ?? this.customer?.fxc
        ?? 0
      ) || 0;
    },
    pendingAmount() {
      return Math.max(this.totalForecast - this.totalBilled, 0);
    },
    collectedPercent() {
      if (this.totalForecast <= 0) {
        return 0;
      }

      return Math.max(0, Math.min(100, toAmount((this.totalBilled / this.totalForecast) * 100)));
    },
    pendingPercent() {
      return Math.max(0, Math.min(100, toAmount(100 - this.collectedPercent)));
    },
    ringPercentLabel() {
      return `${Math.round(this.collectedPercent)}%`;
    },
    ringStyle() {
      const percent = this.collectedPercent;
      const color = this.collectedPercent >= 85
        ? '#1f9d43'
        : (this.collectedPercent >= 70 ? '#d97706' : '#dc2626');

      return {
        background: `conic-gradient(${color} ${percent}%, #dbe2eb ${percent}% 100%)`,
      };
    },
    healthStatusLabel() {
      if (this.pendingPercent >= 35) {
        return this.overviewText.health.status.risk;
      }

      if (this.pendingPercent >= 15) {
        return this.overviewText.health.status.attention;
      }

      return this.overviewText.health.status.healthy;
    },
    healthStatusClass() {
      if (this.pendingPercent >= 35) {
        return 'is-danger';
      }

      if (this.pendingPercent >= 15) {
        return 'is-warning';
      }

      return 'is-success';
    },
    healthSummaryText() {
      if (this.totalForecast <= 0) {
        return this.overviewText.health.noForecastSummary;
      }

      const buildSummary = this.overviewText.health.pendingSummary;
      if (typeof buildSummary === 'function') {
        return buildSummary(formatCurrency(this.pendingAmount), this.pendingPercent.toFixed(1));
      }

      return '';
    },
    showExposureWarning() {
      return this.pendingPercent >= 15;
    },
    exposureWarningText() {
      const buildWarning = this.overviewText.health.exposureWarningText;
      if (typeof buildWarning === 'function') {
        return buildWarning(this.pendingPercent.toFixed(1));
      }

      return '';
    },
    collectedProgressStyle() {
      return {
        width: `${this.collectedPercent}%`,
      };
    },
  },
  methods: {
    formatMoney(value) {
      return formatCurrency(Number(value || 0) || 0);
    },
  },
};
</script>
